import React, { useState, useEffect } from 'react';
import SEO from '../../seo/SEO';
import seoConfig from '../../seo/seoConfig';
import '../styles/HomePage.css';
import { VideoGallerySection, CaseStudySection, PhilosophySection, ServiceCardsCarouselSection, BehindTheScenesSection, ContactCTATwoColumnSection } from '../../ui/sections';
import { HeroBackgroundVideo } from '../../ui/sections/hero';
import { SocialProofSection } from '../../ui/sections/social-proof';
import { ScrollReveal } from '../../ui/components';
import { useI18n } from '../../i18n/I18nProvider';
import heroImage from '../../images/HomePage/HeroSection/HeroSectionImage_Gym_Example.png';
import montajeImage from '../../images/HomePage/ServicesSection/JidduFilms_img_servicios-montajepostproduccion.png';
import direccionImage from '../../images/HomePage/ServicesSection/JidduFilms_img_servicios-direccionvisualyestetica.png';
import comunicacionImage from '../../images/HomePage/ServicesSection/JidduFilms_img_servicios-comunicacionenvivo.png';
import produccionImage from '../../images/HomePage/ServicesSection/JidduFilms_img_servicios-produccion.png';
import btsImage from '../../images/HomePage/ServicesSection/JidduFilms_img_servicios-bts.png';   
import estudioImage from '../../images/HomePage/ServicesSection/JidduFilms_img_servicios-estudioyunidadmovil.png';
import { getVideoGalleryData } from '../data/videoData';
import caseStudyMockup from '../../images/HomePage/CaseStudySection/JidduFilms_ig_mockup.png';
import { getCloudflareUrl } from '../../ui/utils/cloudflareCDN';

// Import logos
import concacafLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_concacaf-color.png';
import balletLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_ballet-color.png';
import goldenlifeLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_goldenlife-color.png';
import leadingLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_leading-color.png';
import letitbeatLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_letitbeat-color.png';
import mediaproLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_mediapro-color.png';
import mpcLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_mpc-color.png';
import nyaLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_nya-color.png';
import pplLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_ppl-color.png';
import skyrosLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_skyros-color.png';
import trapicheLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_trapiche-color.png';
import verizonLogo from '../../images/HomePage/SocialProofSection/JidduFilms_logo-clientes_verizon-color.png';

const HomePage = () => {
  const { t } = useI18n();

  // Helper function to convert **text** to <strong>text</strong>
  const formatTextWithStrong = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const content = part.slice(2, -2);
        return <strong key={index}>{content}</strong>;
      }
      return part;
    });
  };

  const companyLogos = [
    { logo: concacafLogo, name: "CONCACAF" },
    { logo: balletLogo, name: "Ballet Nacional" },
    { logo: goldenlifeLogo, name: "Golden Life" },
    { logo: leadingLogo, name: "Leading" },
    { logo: letitbeatLogo, name: "Let It Beat" },
    { logo: mediaproLogo, name: "MediaPro" },
    { logo: mpcLogo, name: "MPC" },
    { logo: nyaLogo, name: "NYA" },
    { logo: pplLogo, name: "PPL" },
    { logo: skyrosLogo, name: "Skyros" },
    { logo: trapicheLogo, name: "Trapiche" },
    { logo: verizonLogo, name: "Verizon" }
  ];

  // OPTIMIZACIÓN CRÍTICA: Cargar datos de videos inmediatamente para que los thumbnails carguen sin delay
  // Los datos son solo metadata (URLs), no los videos en sí, así que es seguro cargarlos inmediatamente
  // Los videos NO se cargan hasta que el usuario interactúa, pero los thumbnails sí
  const [videoData, setVideoData] = useState(null);

  // Cargar datos de videos inmediatamente
  // OPTIMIZACIÓN: Los datos son solo metadata, no los videos en sí
  // Esto permite que los thumbnails se carguen inmediatamente sin delay
  useEffect(() => {
    // Cargar datos inmediatamente - son solo URLs, no los videos en sí
    // Los videos NO se cargan hasta que el usuario interactúa (hover/click)
    setVideoData(getVideoGalleryData());
  }, []);

  return (
    <div className="landing-page">
      <SEO
        title="Jiddu Films — Creative Production Company in Miami | Visual Direction & Real-Time Content"
        description="JIDDU FILMS is a creative production company based in Miami, focused on visual direction, content creation, and real-time delivery."
        canonical={`${seoConfig.siteUrl}/`}
        image={getCloudflareUrl('https://jiddufilms-bucket.s3.us-east-2.amazonaws.com/jiddu-films-og-image.jpg')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Jiddu Films',
          url: seoConfig.siteUrl,
          logo: getCloudflareUrl('https://jiddufilms-bucket.s3.us-east-2.amazonaws.com/jiddu-films-logo.svg'),
          description: 'JIDDU FILMS is a creative production company based in Miami, focused on visual direction, content creation, and real-time delivery.',
          founder: {
            '@type': 'Person',
            name: 'Matías Piccinini'
          },
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Miami',
            addressRegion: 'FL',
            addressCountry: 'US'
          },
          sameAs: [
            'https://www.instagram.com/jiddufilms',
            'https://www.linkedin.com/company/jiddufilms'
          ]
        }}
      />

      {/* 1. HERO - BACKGROUND VIDEO */}
      <HeroBackgroundVideo
        videoSrc={getCloudflareUrl('https://jiddufilms-bucket.s3.us-east-2.amazonaws.com/JidduFilms_Video_Hero.mp4')}
        poster={getCloudflareUrl('https://s3.amazonaws.com/jiddufilms-hero-videos/hero-poster.jpg')}
        height="100vh"
      />

      {/* 2. VIDEO GALLERY - Carga diferida */}
      <ScrollReveal variant="fade-up" delay={0} duration={500}>
        {videoData ? (
        <VideoGallerySection
          title={t('videoGallery.title')}
          subtitle={t('videoGallery.subtitle')}
          horizontalItems={videoData.horizontalItems}
          verticalItems={videoData.verticalItems}
        />
        ) : (
          <section className="video-gallery">
            <div className="video-gallery__header">
              <h2 className="video-gallery__title">{t('videoGallery.title')}</h2>
              <p className="video-gallery__subtitle">{t('videoGallery.subtitle')}</p>
            </div>
            <div className="video-gallery__placeholder">
              <div className="video-gallery__skeleton-container">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="video-gallery__skeleton-item" />
                ))}
              </div>
            </div>
          </section>
        )}
      </ScrollReveal>


      

      {/* 3. PHILOSOPHY */}
      <ScrollReveal variant="fade-up" delay={50} duration={500}>
        <PhilosophySection 
          title={t('aboutUs.title')}
          subtitle={t('aboutUs.subtitle')}
          leftColumn={[
            <>{formatTextWithStrong(t('aboutUs.leftColumn.paragraph1'))}</>,
            <>{formatTextWithStrong(t('aboutUs.leftColumn.paragraph2'))}</>
          ]}
          rightColumn={[
            <>{formatTextWithStrong(t('aboutUs.rightColumn.paragraph1'))}</>,
            <>{formatTextWithStrong(t('aboutUs.rightColumn.paragraph2'))}</>
          ]}
        />
      </ScrollReveal>

      {/* 4. SERVICES - DRAGGABLE CARDS CAROUSEL */}
      <ScrollReveal variant="fade-up" delay={75} duration={500}>
        <ServiceCardsCarouselSection
          title={t('services.title')}
          subtitle={t('services.subtitle')}
          cards={[
            { 
              title: t('services.editing.title'), 
              subtitle: t('services.editing.subtitle'), 
              image: montajeImage 
            },
            { 
              title: t('services.visualDirection.title'), 
              subtitle: t('services.visualDirection.subtitle'), 
              image: direccionImage 
            },
            { 
              title: t('services.liveCommunication.title'), 
              subtitle: t('services.liveCommunication.subtitle'), 
              image: comunicacionImage 
            },
            { 
              title: t('services.fullProduction.title'), 
              subtitle: t('services.fullProduction.subtitle'), 
              image: produccionImage 
            },
            { 
              title: t('services.studioMobile.title'), 
              subtitle: t('services.studioMobile.subtitle'), 
              image: estudioImage 
            },
            { 
              title: t('services.strategicBts.title'), 
              subtitle: t('services.strategicBts.subtitle'), 
              image: btsImage 
            }
          ]}
        />
      </ScrollReveal>

      {/* 5. CASE STUDY */}
      <ScrollReveal variant="fade-up" delay={100} duration={500}>
        <CaseStudySection 
          title={t('caseStudy.title')}
          clientName={t('caseStudy.clientName')}
          description={<>{formatTextWithStrong(t('caseStudy.description'))}</>}
          mockupImage={caseStudyMockup}
          metrics={[
            {
              value: "+4.7K",
              label: t('caseStudy.metrics.followers'),
              finalValue: 4700,
              suffix: "K",
              prefix: "+"
            },
            {
              value: "+2.8M",
              label: t('caseStudy.metrics.views'),
              finalValue: 2.8,
              suffix: "M",
              prefix: "+"
            },
            {
              value: "77.000",
              label: t('caseStudy.metrics.interactions'),
              finalValue: 77000,
              suffix: "",
              prefix: ""
            },
            {
              value: "+45K",
              label: t('caseStudy.metrics.profileVisits'),
              finalValue: 45000,
              suffix: "K",
              prefix: "+"
            }
          ]}
        />
      </ScrollReveal>

      {/* 6. SOCIAL PROOF */}
      <ScrollReveal variant="fade-up" delay={50} duration={500}>
        <SocialProofSection 
          title={t('clients.title')}
          subtitle={t('clients.subtitle')}
          logos={companyLogos}
          animationDuration={60}
          pauseOnHover={true}
        />
      </ScrollReveal>

      {/* 7. BEHIND THE SCENES */}
      <ScrollReveal variant="fade-up" delay={75} duration={500}>
        <BehindTheScenesSection 
          title={t('behindTheScenes.title')} 
          subtitle={t('behindTheScenes.subtitle')} 
          images={[
            require('../../images/HomePage/BehindTheScenesSection/JidduFilms_img_bts-1.jpg'),
            require('../../images/HomePage/BehindTheScenesSection/JidduFilms_img_bts-2.jpg'),
            require('../../images/HomePage/BehindTheScenesSection/JidduFilms_img_bts-3.jpg'),
            require('../../images/HomePage/BehindTheScenesSection/JidduFilms_img_bts-4.jpg'),
            require('../../images/HomePage/BehindTheScenesSection/JidduFilms_img_bts-5.jpg'),
            require('../../images/HomePage/BehindTheScenesSection/JidduFilms_img_bts-6.jpg'),
            require('../../images/HomePage/BehindTheScenesSection/JidduFilms_img_bts-7.jpg'),
            require('../../images/HomePage/BehindTheScenesSection/JidduFilms_img_bts-8.jpg'),
            require('../../images/HomePage/BehindTheScenesSection/JidduFilms_img_bts-9.jpg')
          ]}
        />
      </ScrollReveal>

      {/* 8. CONTACT CTA - TWO COLUMN */}
      <ScrollReveal variant="fade-up" delay={50} duration={500}>
        <ContactCTATwoColumnSection />
      </ScrollReveal>


    </div>
  );
};

export default HomePage;